import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  requireReason?: boolean;
  loading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = false,
  requireReason = false,
  loading = false
}) => {
  const [reason, setReason] = useState('');
  const [showReasonError, setShowReasonError] = useState(false);

  const handleConfirm = () => {
    if (requireReason && !reason.trim()) {
      setShowReasonError(true);
      return;
    }
    
    onConfirm(reason.trim() || undefined);
    setReason('');
    setShowReasonError(false);
  };

  const handleCancel = () => {
    setReason('');
    setShowReasonError(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {destructive && <AlertTriangle className="h-5 w-5 text-red-600" />}
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {destructive && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This action cannot be undone.
              </AlertDescription>
            </Alert>
          )}

          <p className="text-sm text-muted-foreground">
            {description}
          </p>

          {requireReason && (
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason {requireReason && '*'}
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (showReasonError && e.target.value.trim()) {
                    setShowReasonError(false);
                  }
                }}
                placeholder="Please provide a reason for this action..."
                rows={3}
                className={showReasonError ? 'border-red-500' : ''}
              />
              {showReasonError && (
                <p className="text-xs font-medium text-red-600">
                  A reason is required for this action.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading && (
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
            )}
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};